import Accordion from "@/common/components/Accordion";
import { useState } from "react";
import { SubmitHandler, useFormContext } from "react-hook-form";
import { EstablishmentLeadsFunnelUpdateValidationSchema } from "../../schemas/update.schema";
import { EstablishmentLeadData } from "../../types/establishmentLead.type";
import { useEstablishmentRequiredDocuments } from "@/common/hooks/queries/useEstablishmentRequiredDocuments";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { EstablishmentRequiredDocumentData } from "@/common/types/establishmentRequiredDocument";
import Select from "@/common/components/Select";
import { SearchIcon } from "lucide-react";
import { addDays } from "date-fns";
import MaintenanceRequiredDocumentItem from "./MaintenanceRequiredDocumentItem";
import { formatDate, formatDateInLocalTimezone } from "@/common/utils/formatters";
import Button from "@/common/components/Button";
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl";
import { useInternalFieldArray } from "@/common/hooks/useInternalFieldArray";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";
import { useUpdateEstablishmentLeadMaintenanceRequiredDocuments } from "../../hooks/maintenanceModules/useUpdateEstablishmentLeadMaintenanceRequiredDocuments";
import { useQueryClient } from "@tanstack/react-query";
import { FormTransformers } from "@/common/utils/formTransformers";

interface MaintenanceRequiredDocumentsSectionProps {
  establishmentLead: EstablishmentLeadData;
  disabled?: boolean;
  allowUpdate?: boolean;
}

const MaintenanceRequiredDocumentsSection = ({
  establishmentLead,
  disabled,
  allowUpdate,
}: MaintenanceRequiredDocumentsSectionProps) => {
  const queryClient = useQueryClient();

  const [updateEnabled, setUpdateEnabled] = useState(false);

  const [searchRequiredDocuments, setSearchRequiredDocuments] = useDebounce('');

  const formMethods = useFormContext<EstablishmentLeadsFunnelUpdateValidationSchema>();

  const {
    setValue,
    setError,
    handleSubmit,
  } = formMethods;

  const {
    mutate: updateEstablishmentLeadMaintenanceRequiredDocuments,
    isPending: updateEstablishmentLeadMaintenanceRequiredDocumentsIsPending,
  } = useUpdateEstablishmentLeadMaintenanceRequiredDocuments({
    id: establishmentLead.id,
    establishmentLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLead.id] });

      setUpdateEnabled(false);
    },
    onError: FormTransformers.errorsTransformer<EstablishmentLeadsFunnelUpdateValidationSchema>({
      setError
    }),
  });

  const onSubmit: SubmitHandler<EstablishmentLeadsFunnelUpdateValidationSchema> =
    (data) => updateEstablishmentLeadMaintenanceRequiredDocuments(data);

  const submitMaintenanceModelsRequiredDocument = handleSubmit(onSubmit);

  const {
    fields: maintenanceRequiredDocuments,
    append: appendMaintenanceRequiredDocument,
    remove: removeMaintenanceRequiredDocument,
  } = useInternalFieldArray<EstablishmentLeadsFunnelUpdateValidationSchema, 'maintenance_required_documents_attributes'>({
    ...formMethods,
    name: 'maintenance_required_documents_attributes',
  });

  const {
    data: requiredDocumentsOptions,
  } = useEstablishmentRequiredDocuments({
    filters: {
      search: searchRequiredDocuments,
    },
    mapper: Mapper.mapToOptionsWithItem<EstablishmentRequiredDocumentData>({
      valueFieldName: 'id',
      labelFieldName: 'display_name',
    }),
  });

  const handleAppendRequiredDocument = (requiredDocument: EstablishmentRequiredDocumentData) => {
    appendMaintenanceRequiredDocument({
      document: {
        id: requiredDocument.id,
        display_name: requiredDocument.display_name,
      },
      file_attachment_attributes: {
        blob_id: '',
        file_name: '',
        file_url: '',
      },
      issue_date: '',
      valid_until: requiredDocument.has_default_validity_in_days
        ? requiredDocument.default_validity_in_days
          ? formatDate(addDays(new Date(), requiredDocument.default_validity_in_days))
          : ''
        : '',
    });
  }

  const handleUpdateCancel = () => {
    setValue(
      'maintenance_required_documents_attributes',
      establishmentLead.maintenance_required_documents.map((maintenanceRequiredDocument) => ({
        id: maintenanceRequiredDocument.id,
        document: {
          id: maintenanceRequiredDocument.document_id,
          display_name: maintenanceRequiredDocument.document.display_name,
        },
        file_attachment_attributes: {
          file_name: maintenanceRequiredDocument.file
            ? getFilenameFromUrl(maintenanceRequiredDocument.file.url)
            : '',
          file_url: maintenanceRequiredDocument.file?.url,
        },
        valid_until: maintenanceRequiredDocument.valid_until
          ? formatDateInLocalTimezone(maintenanceRequiredDocument.valid_until)
          : '',
        issue_date: maintenanceRequiredDocument.issue_date
          ? formatDateInLocalTimezone(maintenanceRequiredDocument.issue_date)
          : '',
      }))
    );

    setUpdateEnabled(false);
  }

  return (
    <Accordion
      defaultOpen
      head={(
        <div className="flex items-center justify-between gap-2 w-full mr-2">
          <p>
            Documentos do estabelecimento
          </p>

          {allowUpdate && updateEnabled && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline-error"
                className="py-2 px-4 text-sm font-normal"
                onClick={(event) => {
                  event.stopPropagation();

                  handleUpdateCancel();
                }}
              >
                Cancelar
              </Button>

              <Button
                variant="success"
                className="py-2 px-4 text-sm font-normal"
                onClick={(event) => {
                  event.stopPropagation();

                  submitMaintenanceModelsRequiredDocument?.();
                }}
                isLoading={updateEstablishmentLeadMaintenanceRequiredDocumentsIsPending}
                operationKlassToCheckPermission="Admin::Funnel::Establishment::Leads::UpdateMaintenanceModels"
              >
                Salvar
              </Button>
            </div>
          )}

          {allowUpdate && !updateEnabled && (
            <Button
              className="py-2 px-4 text-sm font-normal"
              onClick={(event) => {
                event.stopPropagation();

                setUpdateEnabled(true);
              }}
              operationKlassToCheckPermission="Admin::Funnel::Establishment::Leads::UpdateMaintenanceModels"
            >
              Editar
            </Button>
          )}
        </div>
      )}
      className="p-4"
      triggerClassName="px-0 py-1 text-[18px] font-bold"
      bodyClassName="pt-4 pb-0"
      body={(
        <div className="flex flex-col gap-4">
          {updateEnabled && (
            <Select
              placeholder={(
                <div className="flex items-center gap-x-2 text-white-800">
                  <SearchIcon strokeWidth={1.5} className="size-5" />

                  <p className="text-sm text-white-300">
                    Pesquisar documentos que possui
                  </p>
                </div>
              )}
              onInputChange={setSearchRequiredDocuments}
              options={requiredDocumentsOptions.filter((requiredDocument) => {
                return !maintenanceRequiredDocuments.some((maintenanceRequiredDocument) => {
                  return maintenanceRequiredDocument.document.id === requiredDocument.value
                });
              })}
              value={null}
              onChange={(newValue) => handleAppendRequiredDocument((newValue as { item: EstablishmentRequiredDocumentData }).item)}
              isDisabled={!updateEnabled || updateEstablishmentLeadMaintenanceRequiredDocumentsIsPending || disabled}
            />
          )}

          {!updateEnabled && !maintenanceRequiredDocuments.length && (
            <EmptyDataMessage className="h-54 overflow-hidden" imageHeight={120} />
          )}

          {maintenanceRequiredDocuments.map((maintenanceRequiredDocument, index) => (
            <MaintenanceRequiredDocumentItem
              key={maintenanceRequiredDocument.document.id}
              index={index}
              maintenanceRequiredDocument={maintenanceRequiredDocument}
              handleRemove={() => removeMaintenanceRequiredDocument(index)}
              disabled={!updateEnabled || updateEstablishmentLeadMaintenanceRequiredDocumentsIsPending || disabled}
              allowUpdate={allowUpdate}
            />
          ))}
        </div>
      )}
    />
  );
}

export default MaintenanceRequiredDocumentsSection;
